/**
 * opticsScoreEngine.ts — pure computation for OPTICS 2026 framework.
 *
 * Formula:
 *   areaWeighted = ind × W_ind + team × W_team + co × W_co       (per employee level)
 *   valueWeighted = average of areaWeighted across the 3 areas
 *   overall      = average of valueWeighted across the 6 OPTICS values
 *
 * Lenses with no score are excluded from the area weighting and the
 * remaining lens weights are normalised so partial submissions still
 * produce a defensible score.
 */

import {
  OPTICS_FRAMEWORK_2026,
  WEIGHTING_BY_LEVEL,
  type EmployeeLevel,
  type OpticsLens,
} from '../types/optics2026';
import type { ReviewResponse } from '../types/magicLink';

const LENSES: readonly OpticsLens[] = ['ind', 'team', 'co'];

/** Average across reviewers for one area, broken down by lens. */
export interface AreaAverage {
  ind: number;
  team: number;
  co: number;
  reviewerCount: number;
}

/** Score breakdown for one OPTICS value with all three of its areas. */
export interface OpticsValueScore {
  code: string;
  title: string;
  areas: Array<{
    id: string;
    title: string;
    indScore: number;
    teamScore: number;
    coScore: number;
    weightedScore: number;
  }>;
  weightedScore: number;
}

export interface OpticsBreakdown {
  level: EmployeeLevel;
  weights: { ind: number; team: number; co: number };
  values: OpticsValueScore[];
  overallScore: number;
}

/**
 * Weighted lens score for one area.
 * Lenses with no positive score are skipped; remaining weights are normalised.
 */
export function weightedAreaScore(
  scores: Partial<Record<OpticsLens, number>>,
  weights: { ind: number; team: number; co: number },
): number {
  const present = LENSES.filter(l => typeof scores[l] === 'number' && (scores[l] as number) > 0);
  if (!present.length) return 0;
  const totalW = present.reduce((s, l) => s + weights[l], 0);
  if (totalW === 0) return 0;
  return present.reduce((s, l) => s + (scores[l] as number) * (weights[l] / totalW), 0);
}

/** Aggregate per-area, per-lens averages from a list of reviewer responses. */
export function aggregateResponses(responses: ReviewResponse[]): Record<string, AreaAverage> {
  type Bucket = { sums: Record<OpticsLens, number>; counts: Record<OpticsLens, number>; reviewers: Set<string> };
  const buckets: Record<string, Bucket> = {};

  for (const r of responses) {
    for (const e of r.entries) {
      const b = buckets[e.areaId] ?? (buckets[e.areaId] = {
        sums:   { ind: 0, team: 0, co: 0 },
        counts: { ind: 0, team: 0, co: 0 },
        reviewers: new Set(),
      });
      for (const lens of LENSES) {
        const v = e.scores[lens];
        if (typeof v === 'number' && v > 0) {
          b.sums[lens]   += v;
          b.counts[lens] += 1;
        }
      }
      b.reviewers.add(r.id);
    }
  }

  const out: Record<string, AreaAverage> = {};
  for (const [areaId, b] of Object.entries(buckets)) {
    out[areaId] = {
      ind:  b.counts.ind  ? b.sums.ind  / b.counts.ind  : 0,
      team: b.counts.team ? b.sums.team / b.counts.team : 0,
      co:   b.counts.co   ? b.sums.co   / b.counts.co   : 0,
      reviewerCount: b.reviewers.size,
    };
  }
  return out;
}

/** Compute the full breakdown from aggregated area averages and an employee level. */
export function computeBreakdown(
  areaAvgs: Record<string, AreaAverage>,
  level: EmployeeLevel,
): OpticsBreakdown {
  const weights = WEIGHTING_BY_LEVEL[level];

  const values: OpticsValueScore[] = OPTICS_FRAMEWORK_2026.map(v => {
    const areas = v.areas.map(a => {
      const s = areaAvgs[a.id] ?? { ind: 0, team: 0, co: 0, reviewerCount: 0 };
      const weightedScore = weightedAreaScore({ ind: s.ind, team: s.team, co: s.co }, weights);
      return {
        id: a.id,
        title: a.title,
        indScore: round(s.ind),
        teamScore: round(s.team),
        coScore: round(s.co),
        weightedScore: round(weightedScore),
      };
    });
    const validAreas = areas.filter(a => a.weightedScore > 0);
    const valueWeighted = validAreas.length
      ? validAreas.reduce((s, a) => s + a.weightedScore, 0) / validAreas.length
      : 0;
    return {
      code: v.code,
      title: v.title,
      areas,
      weightedScore: round(valueWeighted),
    };
  });

  const validValues = values.filter(v => v.weightedScore > 0);
  const overall = validValues.length
    ? validValues.reduce((s, v) => s + v.weightedScore, 0) / validValues.length
    : 0;

  return {
    level,
    weights,
    values,
    overallScore: round(overall),
  };
}

/** Convenience: aggregate + breakdown in one call. */
export function computeReviewerBreakdown(
  responses: ReviewResponse[],
  level: EmployeeLevel,
): OpticsBreakdown {
  return computeBreakdown(aggregateResponses(responses), level);
}

/** Whether enough invitations have been submitted to trigger aggregation. */
export function isAggregationReady(submitted: number, total: number, threshold = 0.80): boolean {
  return total > 0 && submitted / total >= threshold;
}

/** Count how many lens scores a single response has filled (for completeness UX). */
export function responseCompletionPct(response: ReviewResponse): number {
  const total = OPTICS_FRAMEWORK_2026.reduce((s, v) => s + v.areas.length * 3, 0); // 6 × 3 × 3 = 54
  let filled = 0;
  for (const e of response.entries) {
    for (const lens of LENSES) {
      const v = e.scores[lens];
      if (typeof v === 'number' && v > 0) filled++;
    }
  }
  return Math.round((filled / total) * 100);
}

function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}
